import * as qs from 'qs'
import type * as superagent from 'superagent'
import { jsonDateParser } from 'json-date-parser'
import type { RequestArgs, Route, Response, AbortableResponse, AnyEndpoint } from '../http/types.no-deps'
import type { UUID } from '../../shared/lib/typeHelpers.no-deps'
import { methodRequest } from '../../shared/lib/superagent.no-deps'

export type QueryStringArrayFormat = qs.IStringifyOptions['arrayFormat']

export interface HttpRequestArgs<E extends AnyEndpoint> {
    baseUrl: string
    token: string
    route: Route
    requestArgs: RequestArgs<E>
    basicAuth?: boolean
    customerSlug: string | null
    logGroupId?: UUID
    logGroupName?: string
    arrayFormat?: QueryStringArrayFormat
}

function getHttpRequestUrl<E extends AnyEndpoint>(args: HttpRequestArgs<E>) {
    const { params, query } = args.requestArgs as any

    return args.baseUrl + makePath(args.route.pattern, params, query, { arrayFormat: args.arrayFormat })
}

function getHttpRequestHeaders<E extends AnyEndpoint>(args: HttpRequestArgs<E>) {
    const authorization = args.basicAuth
        ? `Basic ${Buffer.from('api:' + args.token).toString('base64')}`
        : 'Bearer ' + args.token

    return {
        Authorization: authorization,
        ...(args.customerSlug ? { 'x-zap-customer-slug': args.customerSlug } : {}),
        ...(args.logGroupId ? { 'x-zap-log-group-id': args.logGroupId } : {}),
        ...(args.logGroupName ? { 'x-zap-log-group-name': args.logGroupName } : {}),
    }
}

export function baseHttpRequestWithFetchAPI<E extends AnyEndpoint>(args: HttpRequestArgs<E>) {
    const { body } = args.requestArgs as any

    const url = getHttpRequestUrl(args)
    const controller = new AbortController()

    const { convertedBody, extraHeaders } =
        body instanceof FormData
            ? {
                  convertedBody: body,
                  extraHeaders: {},
              }
            : {
                  convertedBody: body && JSON.stringify(body),
                  extraHeaders: { 'Content-Type': 'application/json' },
              }

    return {
        responsePromise: fetch(url, {
            method: args.route.method,
            headers: {
                ...getHttpRequestHeaders(args),
                ...extraHeaders,
            },
            signal: controller.signal,
            body: convertedBody,
        }),
        controller,
    }
}

export function baseHttpRequest<E extends AnyEndpoint>(args: HttpRequestArgs<E>) {
    const url = getHttpRequestUrl(args)

    return methodRequest(args.route.method, url).set(getHttpRequestHeaders(args))
}

export function abortableHttpRequest<E extends AnyEndpoint>(args: HttpRequestArgs<E>): AbortableResponse<E> {
    // Need to cast as any as they may not be present in the type
    // Doesn't really matter what they actually are as undefineds are handled
    const { body } = args.requestArgs as any

    const request = baseHttpRequest(args)

    if (!(body instanceof FormData)) {
        request.set({
            'Content-Type': 'application/json',
        })
    }

    return {
        response: request
            .send(body)
            .then((response) => {
                let responseBody = response.text || response.body
                try {
                    responseBody = JSON.parse(response.text, jsonDateParser)
                } catch (e) {
                    // This does not matter it just means it is not json
                }

                return {
                    body: responseBody,
                    status: response.status,
                }
            })
            .catch((error) => {
                const message = error.response?.body?.message ?? error.response?.text ?? error.message
                const status = error.response?.status ?? 500
                throw new HttpRequestError(status, message, error.response)
            }),
        abort: request.abort.bind(request),
    }
}

export class HttpRequestError extends Error {
    constructor(
        public status: number,
        public message: string,
        public response: superagent.Response
    ) {
        super(message)
    }
}

export function isHttpRequestError(error: unknown): error is HttpRequestError {
    return error instanceof HttpRequestError
}

export function httpRequest<E extends AnyEndpoint>(args: HttpRequestArgs<E>): Promise<Response<E>> {
    return abortableHttpRequest(args).response
}

interface MakePathOptions {
    arrayFormat?: QueryStringArrayFormat
}

export function makePath(
    pattern: string,
    params: { [name: string]: any },
    query: { [name: string]: any },
    options: MakePathOptions = {}
) {
    let path = pattern

    if (params != null) {
        for (const k in params) {
            // Throw if param not found
            if (!path.includes(':' + k)) throw new Error(`Pattern "${pattern}" does not contain param "${k}"`)

            // Replace param with data
            path = path.replace(':' + k, params[k].toString())
        }

        // Throw if params still to replace
        if (path.includes(':')) throw new Error(`Failed to replace all params in pattern: ${path}`)
    }

    // Add query to url
    if (query != null) {
        const queryString = qs.stringify(query, { arrayFormat: options?.arrayFormat ?? 'repeat' })
        path += queryString ? `?${queryString}` : ''
    }

    return path
}
